import React, { useEffect } from 'react';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import MenuIcon from '@mui/icons-material/Menu';
import {
    LogoImage,
    AdditionalActionsButton,
    StyledMoreVertOutlinedIcon,
    StyledGetAppOutlinedIcon,
    ModalHeader, ModalTitle, BackBtn
} from "./TopBarRedesigned.styles";
import { NotificationBell } from '../Notifications';
import { registerAndroidBackOverride } from '../../utils/nativeApp';

// T2-6194: opt-in standard cobalt outlined back pill (matches TalentBackButton)
// for surfaces that should use the consistent Talent back button instead of the
// legacy Coral "← Back" (e.g. Edit Profile).
const StandardBackBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.7rem;
    background: transparent;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    color: var(--primary-blue, #0B4598);
    font-family: var(--fontMain, Poppins), sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    svg { font-size: 1.25rem; }
    &:hover { background: rgba(11, 69, 152, 0.04); border-color: var(--primary-blue, #0B4598); }
    &:active { transform: scale(0.98); }
`;

export const YellowBackBtn = ({onClick}: {onClick: () => void}) => {
    return (<BackBtn
        onClick={onClick}
        startIcon={<img
            className="arrowIcon"
            alt="backArrow"
            src={"img/arrowLeftYellow.svg"}
        />}
    >
        <span>Back</span>
    </BackBtn>)
}

export enum AdditionalActionsIconType {
    more = 'more',
    download = 'download',
    /** T2-5854: hamburger / side-menu icon for DO surfaces that own their own
     *  TopBarRedesigned (Locations / Invoices) without needing the full
     *  DentistNavbar header on top. */
    menu = 'menu',
}

const IconsByType = {
    [AdditionalActionsIconType.more]: <StyledMoreVertOutlinedIcon />,
    [AdditionalActionsIconType.download]: <StyledGetAppOutlinedIcon />,
    [AdditionalActionsIconType.menu]: <MenuIcon style={{ color: '#0B4598' }} />,
}

interface TopBarRedesignedProps {
    onBack: () => void;
    title?: string;
    additionalActionsIconType?: AdditionalActionsIconType;
    onAdditionalActionsButtonClick?: () => void;
    isNoBackArrow?: boolean;
    rightActionLabel?: string;
    onRightAction?: () => void;
    rightActionTestId?: string;
    hideNotificationBell?: boolean;
    /** T2-6194: use the standard cobalt outlined back pill instead of the Coral one. */
    useStandardBack?: boolean;
    /** Let App's guarded Android handler own packaged hardware Back. */
    useGlobalAndroidBackHandler?: boolean;
}

const TopBarRedesigned = (
    {
        onBack,
        title,
        onAdditionalActionsButtonClick,
        additionalActionsIconType = AdditionalActionsIconType.more,
        isNoBackArrow,
        rightActionLabel,
        onRightAction,
        rightActionTestId,
        hideNotificationBell = false,
        useStandardBack = false,
        useGlobalAndroidBackHandler = false,
    }: TopBarRedesignedProps) => {

        useEffect(() => {
            if (useGlobalAndroidBackHandler) return undefined;
            return registerAndroidBackOverride(onBack);
        }, [onBack, useGlobalAndroidBackHandler])

    return (
        <ModalHeader>
            {!isNoBackArrow ? (
                useStandardBack ? (
                    <StandardBackBtn onClick={onBack} className="no-fastclick" aria-label="Back">
                        <ArrowBackIcon />
                        <span>Back</span>
                    </StandardBackBtn>
                ) : (
                    <YellowBackBtn onClick={onBack} />
                )
            ) : (
                <div style={{ minWidth: '40px' }} />
            )}
            <ModalTitle>{title || <LogoImage />}</ModalTitle>
            {onRightAction && rightActionLabel ? (
                <BackBtn onClick={onRightAction} className="no-fastclick" data-testid={rightActionTestId}>
                    <span>{rightActionLabel}</span>
                </BackBtn>
            ) : additionalActionsIconType === AdditionalActionsIconType.menu && onAdditionalActionsButtonClick ? (
                /* T2-5854: when the menu icon is used (DO Locations / Invoices),
                   render the NotificationBell alongside the hamburger so the
                   bell stays accessible and the page still has its own
                   side-menu trigger. */
                <div style={{ minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    {!hideNotificationBell && <NotificationBell />}
                    <AdditionalActionsButton className="no-fastclick" onClick={onAdditionalActionsButtonClick}>
                        {IconsByType[additionalActionsIconType]}
                    </AdditionalActionsButton>
                </div>
            ) : onAdditionalActionsButtonClick ? (
                <AdditionalActionsButton className="no-fastclick" onClick={onAdditionalActionsButtonClick}>
                    {IconsByType[additionalActionsIconType]}
                </AdditionalActionsButton>
            ) : hideNotificationBell ? (
                <div style={{ minWidth: '40px' }} />
            ) : (
                <div style={{ minWidth: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                    <NotificationBell />
                </div>
            )}
        </ModalHeader>
    )
}

export default TopBarRedesigned;
